// Списание долга (сценарий приёмки 45). Второй случай, ради которого в разделе оставлена
// модалка: действие необратимое и денежное.
//
// ## Почему окно, а не строка в списке
//
// Списание не «убирает строчку», а признаёт потерю денег: остаток уходит в отчёт о
// невозвратных потерях, и по нему потом спрашивают. Значит подтверждение обязано назвать
// СУММУ до нажатия — это правило продукта, а не оформление. Отменить списание нечем: обратной
// операции у него нет, есть только новый долг.
//
// ## Почему основание длиной не в одно слово
//
// Сервер требует десять знаков, и это не придирка: «списать» в поле основания через полгода не
// объясняет ничего. Проверяем и здесь — чтобы человек узнал об этом до отправки, а не отказом.

import { useState } from 'react';

import { RiDeleteBinLine, RiLockLine } from '@remixicon/react';

import type { DebtOut } from '@/shared/api/endpoints/losses';
import { formatRubles } from '@/shared/money/format';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Modal from '@/shared/ui/modal';
import { TextField } from '@/shared/ui/text-field';

import { useWriteOffDebt } from '../queries';

/** Сколько знаков основания требует сервер. Меньше — отказ, и лучше сказать об этом заранее. */
const МИНИМУМ = 10;

export function WriteOffDialog({
  долг,
  employeeId,
  можно,
}: {
  долг: DebtOut;
  employeeId: string;
  можно: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [основание, setОснование] = useState('');
  const [ошибка, setОшибка] = useState<string | undefined>(undefined);
  const списать = useWriteOffDebt(employeeId);

  function подтвердить() {
    if (основание.trim().length < МИНИМУМ) {
      setОшибка(
        'Основание обязательно и одним словом не годится: списанные деньги попадают в отчёт о невозвратных потерях, и по ним спрашивают.',
      );
      return;
    }
    setОшибка(undefined);
    списать.mutate(
      { debtId: долг.id, basis: основание.trim() },
      { onSuccess: () => setOpen(false) },
    );
  }

  return (
    <Modal.Root
      open={open}
      onOpenChange={(следующее) => {
        setOpen(следующее);
        if (!следующее) {
          списать.reset();
          setОшибка(undefined);
        }
      }}
    >
      <Modal.Trigger asChild>
        <Button.Root variant='neutral' mode='ghost' size='xsmall' type='button'>
          Списать
        </Button.Root>
      </Modal.Trigger>
      <Modal.Content>
        <Modal.Header
          icon={можно ? RiDeleteBinLine : RiLockLine}
          title={можно ? 'Списать долг?' : 'Списать нельзя'}
          description={
            можно
              ? `${долг.debt_type_title}, остаток ${formatRubles(долг.outstanding)}. Действие необратимо: отменить списание нечем, можно только завести долг заново.`
              : 'Списание — исключительное право руководства и администратора: это признание потери денег.'
          }
        />
        <Modal.Body>
          {можно ? (
            <div className='flex flex-col gap-3'>
              <ul className='flex flex-col gap-1.5 text-paragraph-sm text-text-sub-600'>
                <li>
                  Остаток {formatRubles(долг.outstanding)} перестанет удерживаться из зарплаты.
                </li>
                <li>
                  Эти деньги попадут в невозвратные потери за текущий месяц — с типом, суммой и
                  вашим основанием.
                </li>
                <li>Уже удержанное не возвращается: списывается только остаток.</li>
              </ul>
              <TextField
                id='write-off-basis'
                label='Основание списания'
                required
                value={основание}
                onChange={setОснование}
                placeholder='например: человек уволен, взыскание признано невозможным'
                error={ошибка}
                hint='Останется в отчёте о потерях навсегда. Одного слова недостаточно.'
              />
            </div>
          ) : (
            <p className='text-paragraph-sm text-text-sub-600'>
              У вашей учётной записи нет права на списание, поэтому кнопка ничего не изменит.
              Списывают долги руководство и администратор: подготовьте основание и попросите их.
            </p>
          )}
          {списать.isError && <ErrorAlert error={списать.error} className='mt-3' />}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button.Root variant='neutral' mode='stroke' size='small' className='w-full'>
              {можно ? 'Отмена' : 'Понятно'}
            </Button.Root>
          </Modal.Close>
          {можно && (
            <Button.Root
              variant='error'
              mode='filled'
              size='small'
              className='w-full'
              type='button'
              onClick={подтвердить}
            >
              {списать.isPending ? 'Списываем…' : 'Списать'}
            </Button.Root>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}
